import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import useSwitch from "@react-hook/switch";
import { useStatsigClient } from "@statsig/react-bindings";
import { useSnackbar } from "notistack";
import type { FC } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import { Typography } from "@/components/Typography";
import { interlude } from "@/interlude";
import { useWhatsappLogin } from "@/pages/channels/hooks/useWhatsappLogin";
import { ChannelQueriesContext } from "@/pages/channels/pages/detail/channelQueriesContext";
import { ChannelNameEdit } from "@/pages/channels/pages/detail/components/ChannelNameEdit";
import { DisconnectModal } from "@/pages/channels/pages/detail/components/DisconnectModal";
import { FailedConnectModal } from "@/pages/channels/pages/detail/components/FailedConnectModal";
import { evaluateWhatsappReauthResult } from "@/pages/channels/pages/detail/whatsapp/utils/evaluateWhatsappReauthResult";

export const General: FC = function General() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [openFailedConnectModal, toggleFailedConnectModal] = useSwitch();
  const [openDisconnectModal, toggleDisconnectModal] = useSwitch();
  const [failMessage, setFailMessage] = useState<string | undefined>();

  const { channel } = ChannelQueriesContext.useData();
  const updateChannelMutation = interlude.channel.useUpdateChannel({
    params: {
      channelId: channel.id,
    },
  });

  const handleChannelDisconnect = () => {
    updateChannelMutation.mutate(
      {
        status: "disconnected",
      },
      {
        onSuccess() {
          toggleDisconnectModal.off();
          enqueueSnackbar(t("page.channel.whatsapp.disconnect.toast.success"), {
            variant: "success",
          });
        },
      },
    );
  };

  const notifyAuthSuccess = useHandler(() => {
    enqueueSnackbar(t("page.channel.whatsapp.reauthorize.toast.success"), {
      variant: "success",
    });
  });

  const reauthorizeMutation = interlude.channel.useReauthorizeChannel(
    {
      params: {
        channelId: channel.id,
      },
    },
    {
      onSuccess(data) {
        if (data.status === "connected") {
          notifyAuthSuccess();
          return;
        }

        updateChannelMutation.mutate(
          {
            status: "connected",
          },
          {
            onSuccess: notifyAuthSuccess,
            onError: toggleFailedConnectModal.on,
          },
        );
      },
      onError: toggleFailedConnectModal.on,
    },
  );

  const statsigClient = useStatsigClient();
  const enabledCoexistenceWhatsapp = statsigClient.checkGate(
    "whatsapp_embedded_signup_coexistence",
  );

  const loginWhatsappMutation = useWhatsappLogin({
    onSuccess: async ({ signupAuthCode, wabaId, phoneNumberId }) => {
      // If the returned wabaId or phoneNumberId is not equal to the current
      // channel, reauthorization should fail. Surface a specific reason when the
      // returned WABA differs so support can tell why it failed.
      if (channel.type === "whatsapp") {
        const result = evaluateWhatsappReauthResult({
          boundWabaId: channel.channelInformation.wabaInformation?.wabaId,
          boundExternalChannelId: channel.externalChannelId,
          returnedWabaId: wabaId,
          returnedPhoneNumberId: phoneNumberId,
          coexistenceEnabled: enabledCoexistenceWhatsapp,
        });

        if (!result.ok) {
          if (result.reason === "WABA_MISMATCH") {
            setFailMessage(
              t(
                "page.channel.whatsapp.failedConnect.modal.wabaMismatch.description",
                {
                  boundWaba:
                    channel.channelInformation.wabaInformation?.wabaId ?? "-",
                  returnedWaba: wabaId,
                },
              ),
            );
          }
          // PHONE_MISMATCH keeps the generic failed-connect message.
          toggleFailedConnectModal.on();
          return;
        }
      }

      reauthorizeMutation.mutate({
        type: "whatsapp",
        extra: {
          signupAuthCode: signupAuthCode,
        },
      });
    },
    onError: toggleFailedConnectModal.on,
  });

  const handleReauthorize = () => {
    // Start each attempt from a clean slate so a previous WABA-mismatch message
    // is never shown for an unrelated failure on the next attempt.
    setFailMessage(undefined);
    if (channel.type === "whatsapp") {
      loginWhatsappMutation.startLogin({
        extras: {
          // https://developers.facebook.com/docs/whatsapp/embedded-signup/pre-filled-data
          setup: {
            whatsAppBusinessAccount: {
              id: [channel.channelInformation.wabaInformation?.wabaId],
            },
          },
        },
      });
    }
  };

  return (
    <>
      <Stack gap="24px" css={css({ maxWidth: "512px" })}>
        <Stack gap="8px">
          <Typography
            variant="h3"
            fontWeight="medium"
            color={theme.colors.staticFgTitle}
          >
            {t("page.channel.whatsapp.detail.name")}
          </Typography>
          <ChannelNameEdit />
        </Stack>
        <Stack gap="8px">
          <Typography
            variant="h3"
            fontWeight="medium"
            color={theme.colors.staticFgTitle}
          >
            {t("page.channel.whatsapp.detail.token.title")}
          </Typography>
          <Typography variant="note" color={theme.colors.staticFgNote}>
            {t("page.channel.whatsapp.detail.token.description")}
          </Typography>
          {channel.status === "token-expired" ? (
            <InfoBox
              variant="error"
              title={t("page.channel.whatsapp.detail.token.expired.title")}
              description={t(
                "page.channel.whatsapp.detail.token.expired.description",
              )}
            />
          ) : null}
          <Button
            variant="plain"
            onClick={handleReauthorize}
            disabled={channel.status === "disconnected"}
            loading={
              loginWhatsappMutation.isPending || reauthorizeMutation.isPending
            }
          >
            {t("common.reauthorize")}
          </Button>
        </Stack>
        <Stack gap="8px">
          <Typography
            variant="h3"
            fontWeight="medium"
            color={theme.colors.staticFgTitle}
          >
            {t("page.channel.whatsapp.detail.connection.title")}
          </Typography>
          <Typography variant="note" color={theme.colors.staticFgNote}>
            {t("page.channel.whatsapp.detail.connection.description")}
          </Typography>
          {channel.status !== "disconnected" ? (
            <Button
              variant="plain"
              color="error"
              loading={updateChannelMutation.isPending}
              onClick={toggleDisconnectModal.on}
            >
              {t("common.disconnect")}
            </Button>
          ) : (
            <Button
              variant="plain"
              onClick={handleReauthorize}
              loading={
                loginWhatsappMutation.isPending ||
                reauthorizeMutation.isPending ||
                updateChannelMutation.isPending
              }
            >
              {t("common.connect")}
            </Button>
          )}
        </Stack>
      </Stack>
      <FailedConnectModal
        channelType="whatsapp"
        {...(failMessage !== undefined ? { message: failMessage } : {})}
        open={openFailedConnectModal}
        onClose={toggleFailedConnectModal.off}
        onConfirm={toggleFailedConnectModal.off}
      />
      <DisconnectModal
        channelType="whatsapp"
        open={openDisconnectModal}
        isLoading={updateChannelMutation.isPending}
        onClose={toggleDisconnectModal.off}
        onConfirm={handleChannelDisconnect}
      />
    </>
  );
};
